/**
 * Admin API methods (requires admin role)
 *
 * Extracted from api.js. All methods hit /admin/* endpoints.
 */

import apiClient from "./api";

export const adminApi = {
  // Get all plans (including inactive)
  getPlans: async ({ signal } = {}) => {
    const response = await apiClient.get("/admin/plans", { signal });
    return response.data;
  },

  // Create a new plan
  createPlan: async (planData) => {
    const response = await apiClient.post("/admin/plans", planData);
    return response.data;
  },

  // Update an existing plan
  updatePlan: async (id, planData) => {
    const response = await apiClient.put(`/admin/plans/${id}`, planData);
    return response.data;
  },

  // Activate or deactivate a plan
  togglePlanStatus: async (id, isActive) => {
    const response = await apiClient.patch(`/admin/plans/${id}/status`, {
      isActive,
    });
    return response.data;
  },

  // Delete a plan (only if no users on it)
  deletePlan: async (id) => {
    const response = await apiClient.delete(`/admin/plans/${id}`);
    return response.data;
  },

  // Get count of users on a plan
  getPlanUserCount: async (id, { signal } = {}) => {
    const response = await apiClient.get(`/admin/plans/${id}/users`, {
      signal,
    });
    return response.data;
  },

  // Get invites for a private plan
  getInvites: async (planId, { signal } = {}) => {
    const response = await apiClient.get(`/admin/plans/${planId}/invites`, {
      signal,
    });
    return response.data;
  },

  // Create an invite for a private plan
  createInvite: async (planId, { email, expiresInDays }) => {
    const response = await apiClient.post(`/admin/plans/${planId}/invites`, {
      email,
      expiresInDays,
    });
    return response.data;
  },

  // Revoke a pending invite
  revokeInvite: async (inviteId) => {
    const response = await apiClient.patch(`/admin/invites/${inviteId}/revoke`);
    return response.data;
  },

  // Credit pricing
  getCreditPrices: async ({ signal } = {}) => {
    const response = await apiClient.get("/admin/credit-prices", { signal });
    return response.data;
  },

  updateCreditPrice: async (family, creditsPerMessage) => {
    const response = await apiClient.put(`/admin/credit-prices/${family}`, {
      creditsPerMessage,
    });
    return response.data;
  },

  // Credit packages
  getCreditPackages: async ({ signal } = {}) => {
    const response = await apiClient.get("/admin/credit-packages", { signal });
    return response.data;
  },

  createCreditPackage: async (packageData) => {
    const response = await apiClient.post(
      "/admin/credit-packages",
      packageData,
    );
    return response.data;
  },

  updateCreditPackage: async (id, packageData) => {
    const response = await apiClient.put(
      `/admin/credit-packages/${id}`,
      packageData,
    );
    return response.data;
  },

  deleteCreditPackage: async (id) => {
    const response = await apiClient.delete(`/admin/credit-packages/${id}`);
    return response.data;
  },

  // User lookup by ID or email (for credit adjustment preview)
  lookupUser: async (query) => {
    const response = await apiClient.get("/admin/users/lookup", {
      params: { q: query },
    });
    return response.data;
  },

  // User credit adjustment
  adjustUserCredits: async (userId, { modelFamily, amount, description }) => {
    const response = await apiClient.post(`/admin/users/${userId}/credits`, {
      modelFamily,
      amount,
      description,
    });
    return response.data;
  },
};
